'use client'

import { useEffect } from 'react'
import { useSearchParams } from 'next/navigation'
import { useMe } from '@/shared/hooks/useMe'
import { initAnalytics, trackEvent } from '@/shared/utils/analyticsUtils'
import { usePathname } from '@/navigation'
import { isValidMongoDBObjectId, isValidUUID } from '@/shared/utils/validatorUtils'

const getAnonymizedUrl = (url: string): string => {
    const [path, query] = url.split('?')

    const anonymizedPath = path
        .split('/')
        .map((segment) => {
            if (isValidUUID(segment)) return crypto.randomUUID()
            if (isValidMongoDBObjectId(segment))
                return [...Array(24)].map(() => Math.floor(Math.random() * 16).toString(16)).join('')
            return segment
        })
        .join('/')

    if (!query) return anonymizedPath

    const anonymizedQuery = query
        .split('&')
        .map((pair) => {
            const [key, value] = pair.split('=')
            let newValue = value
            if (isValidUUID(value)) newValue = crypto.randomUUID()
            else if (isValidMongoDBObjectId(value))
                newValue = [...Array(24)].map(() => Math.floor(Math.random() * 16).toString(16)).join('')
            return `${key}=${newValue}`
        })
        .join('&')

    return `${anonymizedPath}?${anonymizedQuery}`
}

const AnalyticsProvider = () => {
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const { isGod } = useMe()

    const isActive = !isGod

    useEffect(() => {
        if (isActive) {
            initAnalytics()
        }
    }, [isActive])

    useEffect(() => {
        if (!isActive) return

        const rawUrl = pathname + (searchParams?.toString() ? `?${searchParams}` : '')
        const anonymizedUrl = getAnonymizedUrl(rawUrl)

        trackEvent('Page view', { url: anonymizedUrl })
    }, [pathname, searchParams, isActive])

    return null
}

export default AnalyticsProvider
